import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DealScheduleSummary } from "@/components/schedule/DealScheduleSummary";
import type { BundleDeal } from "@/lib/api/types";
import { formatCents } from "@/lib/utils/number";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal: BundleDeal | null;
};

/** Read-only mirror of BundleDealFormDialog's fields — no editing, no customer preview. */
export function BundleDealDetailDialog({ open, onOpenChange, deal }: Props) {
  // Keeps content rendered through the close animation instead of unmounting mid-transition.
  const [prevDeal, setPrevDeal] = useState(deal);
  const [lastDeal, setLastDeal] = useState(deal);
  if (deal !== prevDeal) {
    setPrevDeal(deal);
    if (deal) setLastDeal(deal);
  }
  const display = deal ?? lastDeal;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        {display && (
          <>
            <DialogHeader>
              <DialogTitle>{display.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Items</p>
                <div className="border rounded-md divide-y divide-border">
                  {display.items.map((it) => (
                    <div
                      key={it.menu_item.id}
                      className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                    >
                      <span className="truncate min-w-0">
                        {it.quantity}× {it.menu_item.name}
                      </span>
                      <span className="text-muted-foreground shrink-0">
                        {formatCents(it.menu_item.price_cents * it.quantity)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Combo Price</p>
                <p className="text-sm font-medium">
                  {formatCents(display.bundle_price_cents)}{" "}
                  <span className="text-muted-foreground line-through font-normal">
                    {formatCents(display.regular_price_cents)}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Savings</p>
                <p className="text-sm font-medium">
                  {formatCents(display.savings_cents)} ({Math.round(display.savings_percent)}%)
                </p>
              </div>
              {display.max_claims_per_customer_per_day != null && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">Claim limit</p>
                  <p className="text-sm font-medium">
                    {display.max_claims_per_customer_per_day}/customer/day
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Schedule</p>
                <DealScheduleSummary schedule={display.schedule} />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Status</p>
                <p className="text-sm font-medium capitalize">{display.status}</p>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
